import React, { useState, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { Card, List, Typography, Input, Select, Space, Tag } from 'antd';
import { SearchOutlined, DatabaseOutlined } from '@ant-design/icons';
import ReportServices from '../../../../services/Report';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const DraggableField = ({ field, entityType }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'field',
    item: { type: 'field', field, entityType },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const isRelationField = field.name.includes('.');
  const relationPrefix = isRelationField ? field.name.split('.')[0] : null;

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        padding: '8px 12px',
        margin: '4px 0',
        background: isRelationField ? '#f0f9ff' : '#f5f5f5',
        border: `1px solid ${isRelationField ? '#bae7ff' : '#d9d9d9'}`,
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderLeft: isRelationField ? '4px solid #1890ff' : 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {isRelationField && (
          <Tag size="small" color="geekblue" style={{ fontSize: '10px' }}>
            {relationPrefix}
          </Tag>
        )}
        <span>{field.label}</span>
      </div>
      <Tag size="small" color={isRelationField ? "geekblue" : "blue"}>
        {field.type}
      </Tag>
    </div>
  );
};

const FieldPalette = ({ onEntityChange }) => {
  const [entities, setEntities] = useState([
    { key: 'items', label: 'Items', fields: [] },
    { key: 'sales', label: 'Sales Invoices', fields: [] },
    { key: 'customers', label: 'Customers', fields: [] },
    { key: 'audit-logs', label: 'Audit Logs', fields: [] }
  ]);
  const [selectedEntity, setSelectedEntity] = useState('items');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // State for field metadata
  const [fieldMetadata, setFieldMetadata] = useState({});

  useEffect(() => {
    loadEntityFields(selectedEntity);
  }, [selectedEntity]);

  const loadEntityFields = async (entityKey) => {
    setLoading(true);
    try {
      // Check if we already have metadata for this entity
      if (fieldMetadata[entityKey]) {
        setEntities(prev => prev.map(entity =>
          entity.key === entityKey
            ? { ...entity, fields: fieldMetadata[entityKey] }
            : entity
        ));
        setLoading(false);
        return;
      }

      // Fetch metadata using ReportServices
      const metadata = await ReportServices.getEntityMetadata(entityKey);
      console.log('Fetched metadata for', entityKey, ':', metadata);

      // Transform metadata to field format
      const fields = metadata.fields.map(field => ({
        name: field.name,
        label: field.label,
        type: field.type,
        operators: field.operators,
        relationTarget: field.relationTarget
      }));

      // Cache the metadata
      setFieldMetadata(prev => ({
        ...prev,
        [entityKey]: fields
      }));

      // Update entities
      setEntities(prev => prev.map(entity =>
        entity.key === entityKey
          ? { ...entity, fields }
          : entity
      ));

    } catch (error) {
      console.error('Error loading entity fields:', error);
      // Fallback to empty array
      setEntities(prev => prev.map(entity =>
        entity.key === entityKey
          ? { ...entity, fields: [] }
          : entity
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (value) => {
    setSelectedEntity(value);
    onEntityChange && onEntityChange(value);
  };

  const handleFieldDoubleClick = (field) => {
    // Allow adding fields from different entities without changing the selected entity
    onEntityChange && onEntityChange(field.entityType || selectedEntity);
  };

  const currentEntity = entities.find(entity => entity.key === selectedEntity);

  // Group fields by relation
  const groupedFields = (currentEntity?.fields || []).reduce((groups, field) => {
    const isRelationField = field.name.includes('.');
    const groupKey = isRelationField ? field.name.split('.')[0] : 'direct';

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(field);
    return groups;
  }, {});

  // Filter and flatten
  const filteredGroups = Object.entries(groupedFields).reduce((result, [groupKey, fields]) => {
    const filteredFields = fields.filter(field =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filteredFields.length > 0) {
      result[groupKey] = filteredFields;
    }
    return result;
  }, {});

  return (
    <Card
      title={
        <Space>
          <DatabaseOutlined />
          Field Palette
        </Space>
      }
      size="small"
      style={{ height: '100%', overflow: 'hidden' }}
    >
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Select
          value={selectedEntity}
          onChange={handleEntityChange}
          style={{ width: '100%' }}
          placeholder="Select Entity"
        >
          {entities.map(entity => (
            <Option key={entity.key} value={entity.key}>
              {entity.label}
            </Option>
          ))}
        </Select>

        <Search
          placeholder="Search fields..."
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%' }}
          prefix={<SearchOutlined />}
        />
      </Space>

      <div style={{
        height: 'calc(100% - 120px)',
        overflow: 'auto',
        border: '1px solid #f0f0f0',
        borderRadius: '4px',
        background: '#fafafa'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading fields...
          </div>
        ) : (
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            {Object.keys(filteredGroups).length > 0 ? (
              <div>
                {Object.entries(filteredGroups).map(([groupKey, fields]) => (
                  <div key={groupKey} style={{ marginBottom: '16px' }}>
                    {groupKey !== 'direct' && (
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#1890ff',
                        textTransform: 'capitalize',
                        marginBottom: '8px',
                        padding: '4px 8px',
                        background: '#f0f9ff',
                        borderRadius: '4px'
                      }}>
                        {groupKey} Fields
                      </div>
                    )}
                    <List
                      size="small"
                      dataSource={fields}
                      renderItem={(field) => (
                        <DraggableField
                          key={field.name}
                          field={{ ...field, entityType: selectedEntity }}
                          entityType={selectedEntity}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No fields found for "{selectedEntity}"
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default FieldPalette;